import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const api = axios.create({ baseURL: API });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Global event for upgrade prompts
let upgradeHandler = null;
export const setUpgradeHandler = (handler) => { upgradeHandler = handler; };

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    if (error.response?.status === 403) {
      const detail = error.response?.data?.detail;
      if (detail?.error === 'upgrade_required' || detail?.error === 'limit_reached') {
        if (upgradeHandler) upgradeHandler(detail);
      }
    }
    return Promise.reject(error);
  }
);

export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  me: () => api.get('/auth/me'),
  demoUsers: () => api.get('/auth/demo-users'),
};

export const dataAPI = {
  dashboardStats: () => api.get('/dashboard-stats'),
  timeline: () => api.get('/timeline'),
  profile: () => api.get('/profile'),
  updateProfile: (data) => api.put('/profile', data),
  coaches: () => api.get('/coaches'),
  jobs: () => api.get('/jobs'),
  job: (id) => api.get(`/jobs/${id}`),
  roadmap: () => api.get('/roadmap'),
  gapAnalysis: () => api.get('/gap-analysis'),
  skills: () => api.get('/skills'),
  addSkill: (name, level) => api.post('/skills', { name, level }),
  deleteSkill: (name) => api.delete(`/skills/${encodeURIComponent(name)}`),
  toggleBookmark: (job_id) => api.post('/bookmarks/toggle', { job_id }),
  bookmarks: () => api.get('/bookmarks'),
  toggleRoadmapStep: (phase, step_index) => api.post('/roadmap/toggle-step', { phase, step_index }),
};

export const aiAPI = {
  strategy: (query, context = '') => api.post('/ai/strategy', { query, context }, { timeout: 120000 }),
  pitch: (query, context = '') => api.post('/ai/pitch', { query, context }, { timeout: 120000 }),
  linkedinMessage: (job_id, contact_name, contact_title, custom_note = '') =>
    api.post('/ai/linkedin-message', { job_id, contact_name, contact_title, custom_note }, { timeout: 120000 }),
  emailOutreach: (job_id, tone = 'professional') =>
    api.post('/ai/email-outreach', { job_id, tone }, { timeout: 120000 }),
  interviewPrep: (job_id, focus = 'general', role = '', company = '') =>
    api.post('/ai/interview-prep', { job_id, focus, role, company }, { timeout: 120000 }),
  history: () => api.get('/ai/history'),
  scrapeJobs: () => api.get('/scrape/jobs', { timeout: 30000 }),
  scrapeJobsUrl: (url) => api.get(url, { timeout: 30000 }),
};

export const interviewAPI = {
  generatePrep: (job_id, focus = 'general', role = '', company = '') =>
    api.post('/interview-prep/generate', { job_id, focus, role, company }, { timeout: 120000 }),
  downloadPrepPDF: (job_id) =>
    `${API}/interview-prep/pdf/${job_id}?token=${localStorage.getItem('token')}`,
  startMock: (job_id, question_type = 'mixed', question_count = 5, role = '', company = '') =>
    api.post('/mock-interview/start', { job_id, question_type, question_count, role, company }, { timeout: 60000 }),
  submitAnswer: (formData) =>
    api.post('/mock-interview/answer', formData, {
      timeout: 120000,
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
  completeMock: (session_id) =>
    api.post(`/mock-interview/complete/${session_id}`, {}, { timeout: 30000 }),
  history: () => api.get('/mock-interview/history'),
  tts: (text) => api.post('/mock-interview/tts', { text }, { timeout: 30000 }),
  companyIntel: (job_id) => api.post('/company-intel', { job_id }, { timeout: 60000 }),
  selfAssessment: (data) => api.post('/mock-interview/self-assessment', data, { timeout: 60000 }),
};

export const jobChatAPI = {
  send: (job_id, message, session_id = null) =>
    api.post('/job-chat', { job_id, message, session_id }, { timeout: 60000 }),
  history: (job_id) => api.get(`/job-chat/${job_id}/history`),
};

export const resumeAPI = {
  get: () => api.get('/resume'),
  save: (data) => api.post('/resume', data),
  upload: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/resume/upload', formData, { timeout: 120000, headers: { 'Content-Type': 'multipart/form-data' } });
  },
  tailor: (job_id) => api.post('/resume/tailor', { job_id }, { timeout: 60000 }),
  atsScoreAI: () => api.post('/resume/ats-score', {}, { timeout: 60000 }),
  exportPDF: (template = 'classic') =>
    `${API}/resume/export/pdf?token=${localStorage.getItem('token')}&template=${template}`,
  exportDOCX: () =>
    `${API}/resume/export/docx?token=${localStorage.getItem('token')}`,
  fitScores: () => api.get('/resume/fit-scores'),
  // Resume Studio new APIs
  aiSuggest: (field, context = {}, current_text = '') =>
    api.post('/resume/ai-suggest', { field, context, current_text }, { timeout: 60000 }),
  wizard: (step, user_input = '', session_data = {}) =>
    api.post('/resume/wizard', { step, user_input, session_data }, { timeout: 60000 }),
  versions: () => api.get('/resume/versions'),
  saveVersion: (name) => api.post('/resume/versions', { name }),
  loadVersion: (id) => api.get(`/resume/versions/${id}`),
  deleteVersion: (id) => api.delete(`/resume/versions/${id}`),
  uploadPhoto: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/resume/photo', formData, { timeout: 30000, headers: { 'Content-Type': 'multipart/form-data' } });
  },
  enhancePhoto: (preset = 'professional') => api.post('/resume/photo/enhance', { preset }, { timeout: 30000 }),
  getPhoto: () => api.get('/resume/photo'),
  highlights: () => api.get('/resume/highlights'),
  syncProfile: () => api.get('/resume/sync-profile'),
  atsScore: () => api.get('/resume/ats-score'),
  magicRegenerate: () => api.post('/resume/magic-regenerate'),
  createShareLink: () => api.post('/resume/share-link'),
  getPublicResume: (shareId) => api.get(`/resume/public/${shareId}`),
  grammarCheck: (text) => api.post('/resume/grammar-check', { text }, { timeout: 60000 }),
  savePreferences: (template, color) => api.put(`/resume/preferences?template=${template}&accent_color=${encodeURIComponent(color)}`),
};

export const compareAPI = {
  compare: (job_ids) => api.post('/jobs/compare', job_ids, { timeout: 30000 }),
};

export const applicationAPI = {
  getAll: () => api.get('/applications'),
  getStatus: (job_id) => api.get(`/applications/${job_id}`),
  updateStatus: (job_id, status, notes = '', follow_up_date = null) =>
    api.post('/applications/status', { job_id, status, notes, follow_up_date }),
};

export const calendarAPI = {
  status: () => api.get('/calendar/status'),
  connect: () => api.get('/calendar/connect'),
  getEvents: () => api.get('/calendar/events'),
  createEvent: (event) => api.post('/calendar/events', event),
  deleteEvent: (id) => api.delete(`/calendar/events/${id}`),
  disconnect: () => api.post('/calendar/disconnect'),
};

export const futureSkillsAPI = {
  get: () => api.post('/future-skills', {}, { timeout: 60000 }),
};

export const aiHistoryAPI = {
  get: () => api.get('/ai-history'),
  delete: (id) => api.delete(`/ai-history/${id}`),
  clearAll: () => api.delete('/ai-history'),
};

export const careerRoadmapAPI = {
  get: () => api.get('/career-roadmap'),
  add: (milestone) => api.post('/career-roadmap', milestone),
  update: (id, milestone) => api.put(`/career-roadmap/${id}`, milestone),
  toggle: (id) => api.patch(`/career-roadmap/${id}/toggle`),
  remove: (id) => api.delete(`/career-roadmap/${id}`),
};

export const remindersAPI = {
  get: () => api.get('/reminders'),
  dailyQuote: () => api.get('/daily-quote'),
};

export const assessmentAPI = {
  list: () => api.get('/assessments/list'),
  start: (assessment_type, domain_id = null) => api.post('/assessments/start', { assessment_type, domain_id }),
  answer: (session_id, item_id, selected_answer, time_ms) =>
    api.post('/assessments/answer', { session_id, item_id, selected_answer, time_ms }),
  results: (assessment_type, domain_id = null) =>
    api.get(`/assessments/results/${assessment_type}`, { params: domain_id ? { domain_id } : {} }),
  allResults: () => api.get('/assessments/results'),
  history: (assessment_type, domain_id = null) =>
    api.get(`/assessments/history/${assessment_type}`, { params: domain_id ? { domain_id } : {} }),
  abandon: (session_id) => api.delete(`/assessments/session/${session_id}`),
  insights: () => api.get('/assessments/insights'),
  actionItems: () => api.post('/assessments/insights/action-items', {}, { timeout: 60000 }),
};

export const gamificationAPI = {
  profile: () => api.get('/gamification/profile'),
  badges: () => api.get('/gamification/badges'),
  leaderboard: () => api.get('/gamification/leaderboard'),
  award: (action) => api.post(`/gamification/award?action=${action}`),
};

export const communityAPI = {
  categories: () => api.get('/community/categories'),
  posts: (params = {}) => api.get('/community/posts', { params }),
  post: (id) => api.get(`/community/posts/${id}`),
  createPost: (data) => api.post('/community/posts', data),
  addComment: (postId, content) => api.post(`/community/posts/${postId}/comments`, { content }),
  vote: (postId, vote_type) => api.post(`/community/posts/${postId}/vote`, { vote_type }),
  deletePost: (postId) => api.delete(`/community/posts/${postId}`),
};

export const subscriptionAPI = {
  plans: () => api.get('/subscriptions/plans'),
  current: () => api.get('/subscriptions/current'),
  features: () => api.get('/subscriptions/features'),
  checkout: (plan_id, origin_url) => api.post('/subscriptions/checkout', { plan_id, origin_url }),
  status: (session_id) => api.get(`/subscriptions/status/${session_id}`),
};

export const exportAPI = {
  gapAnalysisPDF: () => `${API}/export/gap-analysis-pdf?token=${localStorage.getItem('token')}`,
  roadmapPDF: () => `${API}/export/roadmap-pdf?token=${localStorage.getItem('token')}`,
  downloadPDF: async (endpoint) => {
    const res = await api.get(endpoint, { responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = endpoint.includes('gap') ? 'gap_analysis_report.pdf' : 'career_roadmap.pdf';
    link.click();
    window.URL.revokeObjectURL(url);
  },
};

export const linkedinAPI = {
  questions: () => api.get('/linkedin/questions'),
  submit: (answers) => api.post('/linkedin/assessment', { answers }),
  profile: () => api.get('/linkedin/profile'),
  optimizer: () => api.get('/linkedin/optimizer'),
  importData: (data) => api.post('/linkedin/import', data, { timeout: 30000 }),
  getImportData: () => api.get('/linkedin/import'),
};

export const kaireraAPI = {
  getSessions: () => api.get('/kairera/sessions'),
  createSession: (mode = 'full', forceNew = false, goalName = null) => api.post('/kairera/session', { mode, force_new: forceNew, goal_name: goalName }),
  getSession: () => api.get('/kairera/session'),
  submitAnswer: (question_id, answer_value, session_id) => api.post('/kairera/answer', { question_id, answer_value, session_id }),
  getDashboard: (sessionId) => api.get(`/kairera/dashboard${sessionId ? `?session_id=${sessionId}` : ''}`),
  getPanel: (sessionId) => api.get(`/kairera/panel${sessionId ? `?session_id=${sessionId}` : ''}`),
  getPanelInsights: () => api.get('/kairera/analysis/panel-insights'),
  resetSession: () => api.delete('/kairera/session'),
  deleteSession: (sessionId) => api.delete(`/kairera/session/${sessionId}`),
  // Phase 2: Assessment Batteries
  getBatteries: () => api.get('/kairera/assessments/batteries'),
  getBattery: (batteryId) => api.get(`/kairera/assessments/battery/${batteryId}`),
  submitBatch: (batteryId, batch_index, responses) =>
    api.post(`/kairera/assessments/battery/${batteryId}/submit`, { battery_id: batteryId, batch_index, responses }),
  getAssessmentResults: () => api.get('/kairera/assessments/results'),
  getAssessmentInsights: (batteryId) => api.get(`/kairera/assessments/insights/${batteryId}`),
  getAssessmentPersona: () => api.get('/kairera/assessments/persona'),
  getAssessmentComparison: () => api.get('/kairera/assessments/comparison'),
  retakeBattery: (batteryId) => api.post(`/kairera/assessments/retake/${batteryId}`),
  createAssessmentShareLink: () => api.post('/kairera/assessments/share-link'),
  getPublicAssessment: (shareId) => api.get(`/kairera/assessments/public/${shareId}`),
  // Phase 3: Analysis & Reports
  getReportCard: () => api.get('/kairera/analysis/report-card'),
  exportReportPDF: () =>
    `${API}/kairera/analysis/report-card/pdf?token=${localStorage.getItem('token')}`,
  getPersona: () => api.get('/kairera/analysis/persona'),
  getCareerReport: () => api.get('/kairera/analysis/career-report'),
  getMarketDemand: () => api.get('/kairera/analysis/market-demand'),
  getPeerBenchmark: () => api.get('/kairera/analysis/peer-benchmark'),
  getCompanyIntel: () => api.get('/kairera/analysis/company-intel'),
  devilsAdvocate: (message) => api.post('/kairera/analysis/devils-advocate', { message }),
  resetDevilsAdvocate: () => api.delete('/kairera/analysis/devils-advocate'),
  // Interactive Tools
  careerCalculator: (data) => api.post('/kairera/tools/career-calculator', data),
  getGoalsForCalculator: () => api.get('/kairera/tools/goals-for-calculator'),
  negotiate: (data) => api.post('/kairera/tools/negotiate', data),
  resetNegotiation: () => api.delete('/kairera/tools/negotiate'),
  getNegotiationInsights: () => api.get('/kairera/tools/negotiate/insights'),
  textToSpeech: (text, voice) => {
    const formData = new FormData();
    formData.append('text', text);
    formData.append('voice', voice || 'echo');
    return api.post('/kairera/tools/tts', formData);
  },
  speechToText: (audioBlob) => {
    const formData = new FormData();
    formData.append('audio', audioBlob, 'recording.webm');
    return api.post('/kairera/tools/stt', formData);
  },
};

export const referralAPI = {
  getCode: () => api.get('/referral/code'),
  getStats: () => api.get('/referral/stats'),
  apply: (code) => api.post('/referral/apply', { code }),
};

export const notificationAPI = {
  status: () => api.get('/notifications/status'),
  updatePrefs: (prefs) => api.put('/notifications/preferences', prefs),
  sendDigest: () => api.post('/notifications/send-digest'),
  sendReminder: () => api.post('/notifications/assessment-reminder'),
};

export const analyticsAPI = {
  overview: () => api.get('/analytics/overview'),
  users: (skip = 0, limit = 50) => api.get(`/analytics/users?skip=${skip}&limit=${limit}`),
  funnel: () => api.get('/analytics/conversion-funnel'),
  featureUsage: () => api.get('/analytics/feature-usage'),
};

export const resumeTemplateAPI = {
  list: () => api.get('/resume-templates/list'),
  exportPDF: (templateId) => `${API}/resume-templates/export/pdf/${templateId}?token=${localStorage.getItem('token')}`,
  exportDOCX: (templateId) => `${API}/resume-templates/export/docx/${templateId}?token=${localStorage.getItem('token')}`,
};

export default api;
